import React, { useRef, useState, useEffect } from 'react';
import { t } from '@lingui/macro';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { useAnalytics } from 'use-analytics';
import { Wrapper, Name, Icon, Help, ContextMenuAction } from './styled';
import SvgIcon from '../svg-icon/svg-icon';
import { StandardDropdown, MessageBubble } from '../../helpers/icon-dictionary';
import {
  getStudents,
  assignActivityToAllStudents,
  buildAssignActivityToAllStudentsRequestBody
} from './util';
import OutsideClickHandler from '../../helpers/hoc/close/outside-click-handler';
import ContextModal, { ContextModalWidth } from '../context-modal';
import { getContextCoords, getConfiguration } from '../../helpers/utils';
import TopicInfo from '../topic-info';
import InstructionHubMessage, {
  INSTRUCTION_HUB_MESSAGE_CONTEXTS
} from '../instruction-hub-message';
import useInstructionHubMenuGuard from '../instruction-hub-message/use-instruction-hub-menu-guard';
import { getTopics } from '../../containers/course-assign/course-assign-actions';
import { showToast } from '../toast/toast-action';
import {
  ERROR_DETECTED,
  GRID_CELL_SELECTION
} from '../../containers/course-assign/course-assign-types';
import { getLocale } from '../../helpers/locale';
import Resources from '../../helpers/resources';
import { useAssignmentFlowTracking, useTracking } from '../../analytics';
import {
  isFullyInstructionHubBlocked,
  isInstructionHubBlockedAssignment
} from '../../helpers/instruction-hub/instruction-hub-assignments';

function ActivityHeader({
  externalKey,
  activity,
  i18n,
  studentAssignmentIndexes
}) {
  // Do not show preview or teacher notes for these activities
  const blackListedActivities = ['Are you ready?', 'Test'];
  const { track } = useAnalytics();
  const assignFlow = useAssignmentFlowTracking('assign');
  const { trackActivityPreview, trackActivityTeacherNotes } = useTracking();
  const assignmentStatuses = useSelector(
    (state) => state.CourseAssignReducer?.assignmentStatuses,
    shallowEqual
  );
  const classrooms = useSelector(
    (state) => state.CourseAssignReducer?.classrooms,
    shallowEqual
  );
  const courseID = useSelector(
    (state) => state.CourseAssignReducer.activeCourseID
  );
  const classroomId = useSelector(
    (state) => state.CourseAssignReducer.activeClass
  );
  const groupId = useSelector((state) => state.CourseAssignReducer.activeGroup);
  const studentAssignments = useSelector(
    (state) =>
      studentAssignmentIndexes.map(
        (x) => state.CourseAssignReducer?.assignments?.studentAssignments[x]
      ),
    shallowEqual
  );
  const datePreference = useSelector(
    (state) => state.CourseAssignReducer?.datePreference || [],
    shallowEqual
  );
  const instructionHubBlockedLookup = useSelector(
    (state) => state.CourseAssignReducer.instructionHubBlockedLookup
  );
  const assignableStudentAssignments = studentAssignments.filter(
    (studentAssignment) =>
      !isInstructionHubBlockedAssignment(
        studentAssignment,
        instructionHubBlockedLookup
      )
  );
  const isInstructionHubBlocked = isFullyInstructionHubBlocked(
    studentAssignments.length - assignableStudentAssignments.length,
    studentAssignments.length
  );

  const isBlackListed = blackListedActivities
    .map((blackListed) => activity.activityName === blackListed)
    .reduce((prev, next) => prev || next);

  const [showContextMenu, setContextMenu] = useState(false);
  const [callOut, setCallOut] = useState(false);
  const [positionX, setPositionX] = useState(0);
  const [positionY, setPositionY] = useState(0);
  const [loading, setLoading] = useState(false);
  const [positionArrow, setPositionArrow] = useState(0);
  const [positionXCallout, setPositionXCallout] = useState(0);
  const [positionYCallout, setPositionYCallout] = useState(0);
  const [positionArrowCallout, setPositionArrowCallout] = useState(0);
  const [contextTheme, setContextTheme] = useState(null);
  const {
    isInstructionHubMessageShown,
    hideInstructionHubMessage,
    runUnlessInstructionHubBlocked,
    instructionHubContextModalProps
  } = useInstructionHubMenuGuard(isInstructionHubBlocked);
  const dispatch = useDispatch();
  const ljRef = useRef(null);
  const titleRef = useRef(null);
  const helpSeekingStudents = getStudents(
    activity.activityId,
    assignmentStatuses,
    classrooms
  );

  function handleEllipsis() {
    // Multi-Line ellipsis
    if (titleRef.current.innerHTML) {
      const wordArray = titleRef.current.innerHTML.split(' ');
      while (titleRef.current.scrollHeight > titleRef.current.offsetHeight) {
        wordArray.pop();
        titleRef.current.innerHTML = `${wordArray.join(' ')}...`;
      }
    }
  }

  useEffect(() => {
    handleEllipsis();
  }, []);

  const showContextModal = () => {
    const { posX, posY, posArrow } = getContextCoords(
      ljRef.current.getBoundingClientRect(),
      ContextModalWidth
    );
    setPositionY(posY);
    setPositionX(posX);
    setPositionArrow(posArrow);
    setContextMenu(true);
    setContextTheme(null);
    setCallOut(false);
    hideInstructionHubMessage();
  };

  const openPreviewActivity = async () => {
    if (isBlackListed) {
      return;
    }
    trackActivityPreview({ activityId: activity.activityId });
    getConfiguration('ActivityPreviewUrl').then((ActivityPreviewUrl) => {
      window
        .open(
          `${ActivityPreviewUrl}${activity.activityId}?locale=${
            getLocale() || 'en-AU'
          }&token=${Resources.env.authToken}`,
          '_blank'
        )
        .focus();
    });
  };

  const openTeacherNotes = async () => {
    if (isBlackListed) {
      return;
    }
    trackActivityTeacherNotes({ activityId: activity.activityId });
    getConfiguration('ActivityTeacherGuideUrl').then(
      (ActivityTeacherGuideUrl) => {
        window
          .open(
            `${ActivityTeacherGuideUrl}${activity.activityId}.pdf`,
            '_blank'
          )
          .focus();
      }
    );
  };

  const showCallOut = () => {
    if (showContextMenu) {
      return;
    }
    const { posX, posY, posArrow } = getContextCoords(
      ljRef.current.getBoundingClientRect(),
      ContextModalWidth
    );
    setPositionYCallout(posY);
    setPositionXCallout(posX);
    setPositionArrowCallout(posArrow);
    setContextTheme('learningJourney');
    setCallOut(true);
  };

  const hideCallOut = () => {
    setCallOut(false);
  };

  const selectActivity = () => {
    dispatch({
      type: GRID_CELL_SELECTION,
      isActivity: true,
      indexes: studentAssignmentIndexes,
      studentId: 0,
      activity,
      status: 'checked'
    });
    setContextMenu(false);
  };

  const assignToAllStudents = async () => {
    if (loading) {
      return;
    }
    const requestBody = buildAssignActivityToAllStudentsRequestBody(
      assignableStudentAssignments,
      courseID,
      datePreference[0],
      datePreference[1]
    );
    setLoading(true);
    assignFlow.start({
      target: 'class',
      source: 'activity-header',
      action: 'assign-to-all-students',
      activityCount: 1,
      studentCount: assignableStudentAssignments.length,
      dueDate: requestBody.dueDate
    });
    await assignActivityToAllStudents(
      assignableStudentAssignments,
      classroomId,
      groupId,
      courseID,
      datePreference[0],
      datePreference[1]
    ).then((wasSuccessful) => {
      setLoading(false);
      if (wasSuccessful) {
        track('ActivityAssigned');
        assignFlow.confirm({
          target: 'class',
          source: 'activity-header',
          action: 'assign-to-all-students',
          activityCount: 1,
          studentCount: assignableStudentAssignments.length
        });
        dispatch(getTopics(classroomId, groupId));
        dispatch(
          showToast(
            i18n._(
              t(
                'footer.assignSuccess'
              )`Selected student activities have been assigned`
            ),
            'success'
          )
        );
      } else {
        dispatch({
          type: ERROR_DETECTED,
          payload: 'Unable to assign activity to all students'
        });
      }
    });
    setContextMenu(false);
  };

  const handleSelect = (event) => {
    event.stopPropagation();
    runUnlessInstructionHubBlocked(selectActivity);
  };

  const handleAssign = (event) => {
    event.stopPropagation();
    runUnlessInstructionHubBlocked(assignToAllStudents);
  };

  return (
    <Wrapper
      onClick={showContextModal}
      ref={ljRef}
      data-testid="activity-header"
    >
      {helpSeekingStudents.length > 0 ? (
        <Help>
          <div className="iconWrapper">
            <SvgIcon icon={MessageBubble} />
          </div>
          <div className="number">{helpSeekingStudents.length}</div>
        </Help>
      ) : (
        <Icon>
          <SvgIcon icon={StandardDropdown} />
        </Icon>
      )}
      <Name
        data-testid="activity-header-name"
        ref={titleRef}
        onMouseEnter={showCallOut}
        onMouseLeave={hideCallOut}
      >
        {activity.activityName}
      </Name>
      <OutsideClickHandler
        callback={() => setContextMenu(false)}
        forButton={`showContextModal_${activity.activityId}`}
      >
        <ContextModal
          show={showContextMenu}
          positionArrow={positionArrow}
          positionX={positionX}
          positionY={positionY}
          {...instructionHubContextModalProps}
        >
          {isInstructionHubMessageShown ? (
            <InstructionHubMessage
              i18n={i18n}
              context={INSTRUCTION_HUB_MESSAGE_CONTEXTS.ACTIVITY}
              onOpen={() => setContextMenu(false)}
            />
          ) : (
            <>
              <TopicInfo
                standardName={externalKey}
                externalKey={activity.activityName}
                displayHideOption={false}
              />
              <ContextMenuAction
                data-testid="select-all"
                role="button"
                first
                tabIndex="-1"
                blackListed={isInstructionHubBlocked}
                onClick={handleSelect}
              >{i18n.t`Select`}</ContextMenuAction>
              <ContextMenuAction
                data-testid="assign-to-all"
                role="button"
                tabIndex="-1"
                blackListed={isInstructionHubBlocked}
                onClick={handleAssign}
              >{i18n.t`Assign to all students`}</ContextMenuAction>
              <ContextMenuAction
                data-testid="teacher-notes"
                role="button"
                tabIndex="-1"
                blackListed={isBlackListed}
                onClick={openTeacherNotes}
              >{i18n.t`Teacher notes`}</ContextMenuAction>
              <ContextMenuAction
                data-testid="preview-activity"
                role="button"
                tabIndex="-1"
                last
                blackListed={isBlackListed}
                onClick={openPreviewActivity}
              >{i18n.t`Preview activity`}</ContextMenuAction>
            </>
          )}
        </ContextModal>
        <ContextModal
          show={callOut}
          positionArrow={positionArrowCallout}
          positionX={positionXCallout}
          positionY={positionYCallout}
          contextTheme={contextTheme}
          name={activity.activityName}
        />
      </OutsideClickHandler>
    </Wrapper>
  );
}

ActivityHeader.propTypes = {
  activity: PropTypes.object,
  i18n: PropTypes.object,
  externalKey: PropTypes.string,
  studentAssignmentIndexes: PropTypes.arrayOf(PropTypes.number).isRequired
};

export default ActivityHeader;



// WEBPACK FOOTER //
// ./components/activity-header/index.js